import React, { useEffect, useRef, useState } from 'react'
import { Popup } from '@progress/kendo-react-popup'

import { TIMELINE_HOVER_OPEN_DELAY } from '@features/timeSlots/constants'
import HYPER_LINK from '@public/assets/hyperlink_open.svg'

const CARD_CLOSE_DELAY_MS = 200

interface WebResearchQueryCardProps {
  domain?: string
  narrative?: string
  showUrl?: boolean
  terms: string[]
  url?: string
}

export function WebResearchQueryCard({
  domain = '',
  narrative = '',
  showUrl = false,
  terms,
  url = '',
}: WebResearchQueryCardProps) {
  return (
    <div className={`web-query-card${showUrl ? ' web-query-card--time' : ''}`}>
      {showUrl && narrative && (
        <p className="web-query-narrative">{narrative}</p>
      )}
      {showUrl && url && (
        <a
          className="web-query-link"
          href={url}
          rel="noopener noreferrer"
          target="_blank"
          onClick={event => event.stopPropagation()}
        >
          <span className="min-w-0 flex-1 truncate">{domain || url}</span>
          <img alt="" height={16} src={HYPER_LINK} width={16} />
        </a>
      )}
      {terms.length > 0 && (
        <>
          <p className="web-query-heading text-xs font-normal leading-4 tracking-[0.5px] text-[#666]">
            {`Research Query (${terms.length})`}
          </p>
          <ul className="web-query-terms">
            {terms.map(term => (
              <li key={term}>{term}</li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}

interface WebResearchQueryPopoverProps extends WebResearchQueryCardProps {
  anchor: HTMLElement | null
  onMouseEnter?: () => void
  onMouseLeave?: () => void
  open: boolean
}

export function WebResearchQueryPopover({
  anchor,
  onMouseEnter,
  onMouseLeave,
  open,
  ...cardProps
}: WebResearchQueryPopoverProps) {
  const [placedAbove, setPlacedAbove] = useState(false)

  useEffect(() => {
    if (!open) setPlacedAbove(false)
  }, [open])

  if (!open || !anchor) return null

  return (
    <Popup
      anchor={anchor}
      anchorAlign={{ horizontal: 'left', vertical: 'bottom' }}
      animate={false}
      className="web-query-popup"
      collision={{ horizontal: 'fit', vertical: 'flip' }}
      popupAlign={{ horizontal: 'left', vertical: 'top' }}
      popupClass="web-query-popup"
      positionMode="fixed"
      show
      style={{ zIndex: 9999 }}
      onPosition={event => {
        const popup = event.target.element
        if (!popup) return

        const popupRect = popup.getBoundingClientRect()
        const anchorRect = anchor.getBoundingClientRect()
        const popupMid = (popupRect.top + popupRect.bottom) / 2
        const anchorMid = (anchorRect.top + anchorRect.bottom) / 2
        setPlacedAbove(popupMid < anchorMid)
      }}
    >
      <div
        className={`relative ${placedAbove ? 'mb-1.5' : 'mt-1.5'}`}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <span
          aria-hidden
          className={
            placedAbove
              ? 'absolute -bottom-1.5 left-8 h-0 w-0 border-x-[6px] border-t-[6px] border-x-transparent border-t-[#292929]'
              : 'absolute -top-1.5 left-8 h-0 w-0 border-x-[6px] border-b-[6px] border-x-transparent border-b-[#292929]'
          }
        />
        <WebResearchQueryCard {...cardProps} />
      </div>
    </Popup>
  )
}

export function useDelayedHover(enabled: boolean) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const openRef = useRef(false)
  const [open, setOpen] = useState(false)

  const clearTimer = () => {
    if (!timerRef.current) return
    clearTimeout(timerRef.current)
    timerRef.current = null
  }

  const setHoverOpen = (next: boolean) => {
    openRef.current = next
    setOpen(next)
  }

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  return {
    open: enabled && open,
    onMouseEnter: () => {
      if (!enabled) return
      clearTimer()
      if (openRef.current) {
        setHoverOpen(true)
        return
      }
      timerRef.current = setTimeout(() => {
        timerRef.current = null
        setHoverOpen(true)
      }, TIMELINE_HOVER_OPEN_DELAY)
    },
    onMouseLeave: () => {
      if (!enabled) return
      clearTimer()
      timerRef.current = setTimeout(
        () => setHoverOpen(false),
        CARD_CLOSE_DELAY_MS
      )
    },
  }
}
